"use client";

import { Chevron } from "@faire/web/slate/icons/foundation/Chevron";
import { Typography } from "@faire/web/slate/Typography";
import { Collapsable } from "@faire/web/ui/Collapsable/Collapsable";
import * as React from "react";
import styled from "styled-components";

import { getSpacing, GrowingSpacer } from "../spacing";

/** An AccordionItem is an object containing a question and an answer as React Nodes. If you Pass in a string, then default Typogrpahy Styles will be applied */
export type AccordionItem = {
  /** Usually represents the question being asked */
  question: React.ReactNode;
  /** Usually represents the answer to the question */
  answer: React.ReactNode;
};

export type AccordionItemProps = AccordionItem & {
  /** Tracks whether a question is currently expanded */
  isCollapsed: boolean;
  /** Callback function that is triggered when an AccordionItem is clicked */
  onClick: () => void;
  /** The test id */
  "data-test-id"?: string;
};

/** Single instance of a frequently asked question. Meant to be used as a part of Accordion and not individually  */
export const AccordionListItem: React.FC<
  AccordionItemProps & React.RefAttributes<HTMLButtonElement>
> = ({
  question,
  answer,
  isCollapsed,
  onClick,
  "data-test-id": testId,
  ref,
}) => (
  <Container data-test-id={testId}>
    <QuestionContainer
      type="button"
      onClick={onClick}
      ref={ref}
      data-test-id={testId && `${testId}__question`}
    >
      {typeof question === "string" ? (
        <Typography variant="subheadingSansRegular">{question}</Typography>
      ) : (
        question
      )}
      <GrowingSpacer base="3x" />
      <Chevron direction={isCollapsed ? "down" : "up"} />
    </QuestionContainer>
    <AnswerContainer
      isCollapsed={isCollapsed}
      data-test-id={testId && `${testId}__answer`}
    >
      {typeof answer === "string" ? <Typography>{answer}</Typography> : answer}
    </AnswerContainer>
  </Container>
);

const Container = styled.div`
  box-sizing: border-box;
  width: 100%;
  cursor: pointer;
`;

const AnswerContainer = styled(Collapsable)`
  padding-left: ${getSpacing("3x")};
  padding-right: ${getSpacing("3x")};
  padding-bottom: ${({ isCollapsed }) => getSpacing(isCollapsed ? 0 : "2x")};
  cursor: initial;
`;

const QuestionContainer = styled.button`
  display: flex;
  flex-direction: row;
  width: 100%;
  align-items: center;
  text-align: left;
  justify-content: space-between;
  padding: ${getSpacing("2x", "3x")};
`;
